// src/components/Navbar.jsx
import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const GOLD = "#C89B3C";
// sticky header height (Tailwind h-16 => 64px). Add a little buffer.
const NAV_OFFSET_PX = 72;

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Single active menu: "lawsuits" | "about" | "resources" | null
  const [activeMenu, setActiveMenu] = useState(null);
  const hoverTimer = useRef(null);

  const onEnter = (key) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setActiveMenu(key);
  };
  const onLeave = () => {
    hoverTimer.current = setTimeout(() => setActiveMenu(null), 160);
  };

  // route tracking
  const location = useLocation();

  // Close any open menu on route change
  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // ===== Scroll management (global, simple, robust) =====
  // 1) Scroll to top on pathname change
  useEffect(() => {
    // When path changes (new page), go to the top immediately.
    // We do NOT interfere with hash scrolling in this effect.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  // 2) Scroll to anchor on hash change (with sticky navbar offset)
  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.slice(1);
    let tries = 0;
    const maxTries = 12;

    const scrollToAnchor = () => {
      const el = document.getElementById(id);
      if (el) {
        const y =
          el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET_PX;
        window.scrollTo({ top: y, left: 0, behavior: "smooth" });
        return;
      }
      if (tries < maxTries) {
        tries += 1;
        setTimeout(scrollToAnchor, 50); // wait for layout/images
      }
    };

    // wait a frame to ensure DOM is painted
    requestAnimationFrame(scrollToAnchor);
  }, [location.hash]);

  // mobile list (unchanged labels; links fixed to go to home anchors)
  const torts = [
    { label: "Camp Lejeune", to: "/#camp-lejeune" },
    { label: "Roundup (Glyphosate)", to: "/#roundup" },
    { label: "Hair Relaxer", to: "/#hair-relaxer" },
    { label: "Talcum Powder", to: "/#talc" },
  ];

  // ===== Lawsuits mega-menu data =====
  const megaMenu = [
    {
      heading: "Dangerous & Defective Products",
      items: [
        {
          label: "Baby Formula NEC Lawsuits",
          url: "/lawsuits/baby-formula-nec",
        },
      ],
    },
    {
      heading: "Exposure to Toxic Substances",
      items: [
        { label: "RoundUp Cancer Lawsuits", 
          url: "/lawsuits/roundup-cancer" },
        {
          label: "Chemical Hair Relaxers and Straighteners Cancer",
          url: "/lawsuits/hair-relaxer-cancer",
        },
        {
          label: "Paraquat Herbicide Lawsuits",
          url: "/lawsuits/paraquat-parkinsons",
        },
        {
          label: "Lung Cancer Asbestos Lawsuit",
          url: "/lawsuits/asbestos-lung-cancer",
        },
        {
          label: "PFAS Water Contamination Lawsuits",
          url: "/lawsuits/pfas-water",
        },
        { label: "Los Angeles Wildfire", 
          url: "/lawsuits/la-wildfire" 
        },
        { label: "Silicosis Lawsuit", 
          url: "/lawsuits/silicosis" 
        },
      ],
    },
    {
      heading: "Defective Drugs & Medical Devices",
      items: [
        { label: "Oxbryta Lawsuits", url: "/lawsuits/oxbryta" },
        {
          label: "Bard PowerPort Catheter Lawsuits",
          url: "/lawsuits/bard-powerport",
        },
        { label: "Hernia Mesh Lawsuits", url: "/lawsuits/hernia-mesh" },
        {
          label: "Ozempic and Wegovy Lawsuits",
          url: "/lawsuits/ozempic-wegovy",
        },
        { label: "Depo Provera Lawsuits", url: "/lawsuits/depo-provera" },
        { label: "Suboxone Lawsuit", url: "/lawsuits/suboxone" },
        { label: "Dupixent Lawsuit", url: "/lawsuits/dupixent" },
      ],
    },
    {
      heading: "Injury & Disability",
      items: [
        {
          label: "Auto Accident and Personal Injury Lawsuits",
          url: "/lawsuits/auto-accident-personal-injury",
        },
        {
          label: "Social Security Disability Benefits (SSDI) Applications",
          url: "/lawsuits/ssdi-applications",
        },
        { label: "Birth Injury Lawyers", url: "/lawsuits/birth-injury" },
        {
          label: "Video Game Addiction Lawsuits",
          url: "/lawsuits/video-game-addiction",
        },
      ],
    },
    {
      heading: "Physical & Sexual Abuse",
      items: [
        {
          label: "LDS Mormon Church Abuse Lawsuit",
          url: "/lawsuits/lds-abuse",
        },
        {
          label: "Roblox & Discord Child Abuse Lawsuit",
          url: "/lawsuits/roblox-discord-child-abuse",
        },
        {
          label: "Rideshare Abuse and Assault Lawsuits",
          url: "/lawsuits/rideshare-assault",
        },
        { label: "Snapchat Addiction Lawsuit", 
          url: "/lawsuits/snapchat-addiction"
        },
      ],
    },
  ];

  // helper: close menus on any link click
  const closeAll = () => {
    setActiveMenu(null);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#1F2A44] text-white/95 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 font-bold tracking-tight"
          onClick={closeAll}
        >
          <span className="text-xl">⚖️</span>
          <span className="text-lg md:text-xl">MassTortPlaintiffs</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/" className="hover:text-white" onClick={closeAll}>
            Home
          </Link>

          {/* Lawsuits */}
          <div
            className="relative"
            onMouseEnter={() => onEnter("lawsuits")}
            onMouseLeave={onLeave}
            onFocusCapture={() => onEnter("lawsuits")}
            onBlur={onLeave}
          >
            <button className="inline-flex items-center gap-1 hover:text-white">
              Lawsuits
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                className="opacity-80"
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </button>

            {/* hover bridge */}
            <div className="absolute left-0 right-0 top-full h-4" aria-hidden />

            {activeMenu === "lawsuits" && (
              <div
                role="menu"
                className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+16px)] z-50
                           w-[min(1120px,96vw)] rounded-2xl bg-white text-[#0A2540]
                           border border-black/10 shadow-2xl max-h-[70vh] overflow-y-auto overscroll-contain"
              >
                {/* subtle premium background */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `
                      radial-gradient(900px 320px at 0% 0%, rgba(200,155,60,0.08) 0%, rgba(200,155,60,0) 60%),
                      radial-gradient(900px 320px at 100% 100%, rgba(10,37,64,0.08) 0%, rgba(10,37,64,0) 60%),
                      linear-gradient(180deg, rgba(10,37,64,0.02), rgba(10,37,64,0))
                    `,
                  }}
                />

                {/* 5 columns on xl */}
                <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 xl:gap-6 p-5">
                  {megaMenu.map((col, idx) => (
                    <div
                      key={col.heading}
                      className={
                        idx > 0 ? "md:border-l md:border-slate-100 md:pl-5" : ""
                      }
                    >
                      <div className="text-base font-semibold text-[#0A2540] flex items-center gap-2">
                        <span
                          className="inline-block h-1.5 w-6 rounded-full"
                          style={{ background: GOLD }}
                        />
                        {col.heading}
                      </div>
                      <ul className="mt-3 space-y-1">
                        {col.items.map((it) => (
                          <li key={it.url}>
                            <Link
                              to={it.url}
                              className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 hover:text-[#C89B3C] transition"
                              onClick={closeAll}
                            >
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C89B3C] flex-shrink-0" />
                              <span className="text-sm leading-6 underline decoration-transparent hover:decoration-[#C89B3C] decoration-2 underline-offset-2">
                                {it.label}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* About */}
          <div
            className="relative"
            onMouseEnter={() => onEnter("about")}
            onMouseLeave={onLeave}
            onFocusCapture={() => onEnter("about")}
            onBlur={onLeave}
          >
            <Link
              to="/about"
              className="hover:text-white inline-flex items-center gap-1"
              onClick={closeAll}
            >
              About Us
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                className="opacity-80"
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </Link>

            <div className="absolute left-0 right-0 top-full h-4" aria-hidden />

            {activeMenu === "about" && (
              <div
                role="menu"
                className="absolute left-0 top-[calc(100%+16px)] z-50 w=[min(560px,92vw)]
                           rounded-2xl bg-white text-[#0A2540] border border-black/10 shadow-2xl overflow-hidden"
                style={{ width: "min(560px,92vw)" }}
              >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#FFF8E7] via-white to-white" />
                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
                  <div>
                    <div className="text-lg font-semibold">Who we are</div>
                    <p className="mt-2 text-sm text-[#0A2540]/80">
                      We help injured consumers understand options and match
                      with vetted mass-tort teams. Fast, private reviews and
                      clear next steps—no pressure, no upfront costs.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {[
                      "Nationwide partner firms",
                      "No upfront fees",
                      "Fast eligibility check",
                    ].map((t) => (
                      <div key={t} className="flex items-start gap-2">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C89B3C]" />
                        <span className="text-sm leading-6">{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative border-t border-slate-100 px-5 py-3 text-xs text-slate-500 flex items-center justify-between">
                  <span>100% confidential • SSL secure</span>
                  <Link
                    to="/about"
                    onClick={closeAll}
                    className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold text-white bg-[#C89B3C] hover:bg-[#B8872D] transition"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Resources */}
          <div
            className="relative"
            onMouseEnter={() => onEnter("resources")}
            onMouseLeave={onLeave}
            onFocusCapture={() => onEnter("resources")}
            onBlur={onLeave}
          >
            <button className="inline-flex items-center gap-1 hover:text-white">
              Resources
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                className="opacity-80"
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </button>

            <div className="absolute left-0 right-0 top-full h-4" aria-hidden />

            {activeMenu === "resources" && (
              <div
                role="menu"
                className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+16px)] z-50
                           w-[min(760px,92vw)] rounded-2xl bg-white text-[#0A2540]
                           border border-black/10 shadow-2xl"
              >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#FFF8E7] via-white to-white" />
                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 p-5">
                  {/* Blog */}
                  <div>
                    <div className="text-base font-semibold flex items-center gap-2">
                      <span
                        className="inline-block h-1.5 w-6 rounded-full"
                        style={{ background: GOLD }}
                      />
                      Blog
                    </div>
                    <p className="mt-2 text-sm text-[#0A2540]/80">
                      Insights on investigations, timelines, and legal updates.
                    </p>
                    <div className="mt-3 text-sm">
                      <Link
                        to="/blog"
                        onClick={closeAll}
                        className="hover:text-[#C89B3C] underline-offset-2"
                      >
                        Visit Blog
                      </Link>
                    </div>
                  </div>

                  {/* Newsletter */}
                  <div>
                    <div className="text-base font-semibold flex items-center gap-2">
                      <span
                        className="inline-block h-1.5 w-6 rounded-full"
                        style={{ background: GOLD }}
                      />
                      Newsletter
                    </div>
                    <ul className="mt-2 space-y-1">
                      {[
                        "Active investigations snapshot",
                        "Eligibility criteria changes",
                        "Deadlines & settlement news",
                      ].map((t) => (
                        <li key={t} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C89B3C]" />
                          <span className="text-sm leading-6">{t}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex items-center gap-3 text-sm">
                      <Link
                        to="/newsletter"
                        onClick={closeAll}
                        className="hover:text-[#C89B3C] underline-offset-2"
                      >
                        Read Newsletter
                      </Link>
                      <span className="text-slate-300">|</span>
                      <Link
                        to="/newsletter#subscribe"
                        onClick={closeAll}
                        className="hover:text-[#C89B3C] underline-offset-2"
                      >
                        Subscribe
                      </Link>
                    </div>
                  </div>

                  {/* FAQs */}
                  <div>
                    <div className="text-base font-semibold flex items-center gap-2">
                      <span
                        className="inline-block h-1.5 w-6 rounded-full"
                        style={{ background: GOLD }}
                      />
                      FAQs
                    </div>
                    <ul className="mt-2 space-y-1">
                      {[
                        "What is a mass tort?",
                        "Do I pay any fees upfront?",
                        "How long does a review take?",
                      ].map((t) => (
                        <li key={t} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C89B3C]" />
                          <span className="text-sm leading-6">{t}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 text-sm">
                      <Link
                        to="/faqs"
                        onClick={closeAll}
                        className="hover:text-[#C89B3C] underline-offset-2"
                      >
                        See all FAQs
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Link to="/contact" className="hover:text-white" onClick={closeAll}>
            Contact Us
          </Link>
        </nav>

        {/* CTA */}
        <Link
          to="/contact"
          onClick={closeAll}
          className="hidden md:inline-flex items-center rounded-full px-4 py-2 font-semibold text-white bg-[#C89B3C] hover:bg-[#B8872D] transition"
        >
          Free Case Review
        </Link>

        {/* Mobile toggle */}
        <button
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-white/10"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path
              d="M4 6h16M4 12h16M4 18h16"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#1F2A44]">
          <nav className="max-w-6xl mx-auto px-4 py-3 space-y-2">
            <Link to="/" className="block py-2" onClick={closeAll}>
              Home
            </Link>

            <details className="group">
              <summary className="cursor-pointer list-none py-2 flex items-center justify-between">
                <span>Lawsuits</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  className="transition group-open:rotate-180"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </summary>
              <ul className="pl-4 pb-2 space-y-1">
                {torts.map((t) => (
                  <li key={t.label}>
                    <Link
                      to={t.to}
                      className="block py-1.5 opacity-90 hover:opacity-100"
                      onClick={closeAll}
                    >
                      {t.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </details>

            <Link to="/about" className="block py-2" onClick={closeAll}>
              About Us
            </Link>
            <Link to="/blog" className="block py-2" onClick={closeAll}>
              Blog
            </Link>
            <Link to="/newsletter" className="block py-2" onClick={closeAll}>
              Newsletter
            </Link>
            <Link to="/faqs" className="block py-2" onClick={closeAll}>
              FAQs
            </Link>
            <Link to="/contact" className="block py-2" onClick={closeAll}>
              Contact Us
            </Link>

            <Link
              to="/contact"
              onClick={closeAll}
              className="mt-2 inline-flex items-center rounded-full px-4 py-2 font-semibold text-white bg-[#C89B3C] hover:bg-[#B8872D] transition"
            >
              Free Case Review
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
